import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Paper,
  Text,
  PageHeader,
  Group,
  VIPSO_RED,
} from '@vipso/shared';
import {
  IconUsers,
  IconBuildingStore,
  IconUserCheck,
  IconBuildingWarehouse,
  IconMapPin,
  IconTruck,
  IconHammer,
  IconSettings,
  IconChevronRight,
  IconShieldHalfFilled,
  IconHierarchy2,
  IconArrowsTransferDown,
} from '@tabler/icons-react';
import { useModuleLabel } from '../hooks/useModuleLabel';
import { useNavAccess } from '../hooks/useNavAccess';

type MoreTile = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number | string; stroke?: number; color?: string }>;
};

export function MobileMorePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const nav = useNavAccess();
  const customersLabel = useModuleLabel('customer');
  const vendorsLabel = useModuleLabel('vendor');

  const tiles = useMemo(() => {
    const list: MoreTile[] = [];
    if (nav.customers) {
      list.push({ to: '/customers', label: customersLabel, icon: IconUsers });
    }
    if (nav.vendors) {
      list.push({ to: '/vendors', label: vendorsLabel, icon: IconBuildingStore });
    }
    if (nav.staff) {
      list.push({ to: '/staff', label: t('nav.staff'), icon: IconUserCheck });
    }
    if (nav.roles) {
      list.push({ to: '/roles', label: t('nav.roles'), icon: IconShieldHalfFilled });
    }
    if (nav.approvalFlows) {
      list.push({
        to: '/approval-flows',
        label: t('nav.approvalFlows'),
        icon: IconHierarchy2,
      });
    }
    if (nav.warehouse) {
      list.push({
        to: '/warehouse',
        label: t('nav.warehouse'),
        icon: IconBuildingWarehouse,
      });
    }
    if (nav.storage) {
      list.push({ to: '/storage', label: t('nav.storage'), icon: IconMapPin });
    }
    if (nav.inventory) {
      list.push({
        to: '/inventory',
        label: t('nav.inventory'),
        icon: IconArrowsTransferDown,
      });
    }
    if (nav.transport) {
      list.push({ to: '/transport', label: t('nav.transport'), icon: IconTruck });
    }
    if (nav.labour) {
      list.push({ to: '/labour', label: t('nav.labour'), icon: IconHammer });
    }
    return list;
  }, [t, nav, customersLabel, vendorsLabel]);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('nav.more')}
        description={t('nav.moreDescription')}
      />

      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        {tiles.map((tile) => {
          const IconCmp = tile.icon;
          return (
            <Box
              key={tile.to}
              component="button"
              type="button"
              onClick={() => navigate(tile.to)}
              style={{
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: 14,
                background: '#fff',
                padding: 18,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                textAlign: 'center',
                minHeight: 120,
                boxShadow: '0 2px 10px rgba(0,14,33,0.04)',
              }}
            >
              <Box
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'var(--mantine-color-brand-0)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <IconCmp size={24} stroke={1.75} color={VIPSO_RED} />
              </Box>
              <Text size="sm" fw={600} c="navy.9">
                {tile.label}
              </Text>
            </Box>
          );
        })}
      </Box>

      <Paper
        component="button"
        type="button"
        onClick={() => navigate('/settings')}
        p="md"
        radius="md"
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          background: '#fff',
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="md" wrap="nowrap">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'var(--mantine-color-brand-0)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <IconSettings size={20} stroke={1.75} color={VIPSO_RED} />
            </Box>
            <Text fw={600}>{t('nav.settings')}</Text>
          </Group>
          <IconChevronRight size={18} stroke={1.75} />
        </Group>
      </Paper>
    </Stack>
  );
}
